import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { dashboardService } from "../../services/dashboardService";
import { useCurrency } from "../../hooks/useCurrency";
import useMinimumLoader from "../../hooks/useMinimumLoader";
import SegmentedControl from "../../components/common/SegmentedControl";
import RowActionsMenu from "../../components/common/RowActionsMenu";
import StatCard from "../../components/common/StatCard";
import CustomDropdown from "../../components/forms/CustomDropdown";
import Toast from "../../components/Toast";
import chrome from "../../components/common/PageChrome.module.css";
import ui from "./DashboardUI.module.css";
import Panel from "./home/Panel";
import { Donut } from "./DashboardCharts";
import BarChart from "./reports/BarChart";
import BarList from "./reports/BarList";
import ReportTable from "./reports/ReportTable";
import { UTIL_TARGET, utilColor, hoursColor } from "./reports/cells";
import styles from "./reports/Reports.module.css";
import { errorMessage } from "../../utils/errorMessage";
import { formatCompact } from "../../utils/costUtils";
import ExportDialog from "../../components/common/ExportDialog";
import { exportWorkbook } from "../../utils/tableExport";
import { REPORT_TABS, PERIODS, PEOPLE, TAB_LAYOUT, tabCards, tableSheet, summarySheet, tablesOf } from "./reports/reportsConfig";

const BLANK_FILTERS = { client_id: "", people: "all", employee_id: "" };
const PALETTE = ["#0C447C", "#185FA5", "#378ADD", "#85B7EB", "#BFD8F2", "#cbd5e1"];
const REASON_COLORS = ["#dc2626", "#f59e0b", "#378ADD", "#0F6E56", "#94a3b8", "#cbd5e1"];
const STAGE_COLOR = (p) => (p >= 0.75 ? "#185FA5" : p >= 0.5 ? "#378ADD" : "#f59e0b");
const round1 = (v) => Math.round((Number(v) || 0) * 10) / 10;

// A donut with its legend: top five slices plus Other.
function DonutPanel({ title, sub, slices, label, centerSub, show, empty }) {
  const top = slices.slice(0, 5);
  const rest = slices.slice(5).reduce((acc, s) => acc + s.value, 0);
  const shown = [...top, ...(rest ? [{ name: "Other", value: rest }] : [])].map((s, i) => ({ ...s, color: s.color || PALETTE[i] }));
  const total = shown.reduce((acc, s) => acc + s.value, 0);
  return (
    <Panel title={title} sub={sub}>
      {total ? (
        <div className={styles.donutRow}>
          <Donut segments={shown.map((s) => ({ value: s.value, color: s.color }))} label={label(total)} sub={centerSub} size={160} thickness={20} />
          <ul className={styles.donutLegend}>
            {shown.map((s) => <li key={s.name}><i style={{ background: s.color }} />{s.name}<span>{show(s.value, total)}</span></li>)}
          </ul>
        </div>
      ) : <p className={styles.empty}>{empty}</p>}
    </Panel>
  );
}

export default function Reports() {
  const [params, setParams] = useSearchParams();
  const tab = REPORT_TABS.some((t) => t.value === params.get("report")) ? params.get("report") : "overview";
  const [period, setPeriod] = useState("ytd");
  const [filters, setFilters] = useState(BLANK_FILTERS);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const { symbol, format } = useCurrency();
  const showLoading = useMinimumLoader(loading && !report);

  useEffect(() => {
    let live = true;
    setLoading(true);
    dashboardService.getReports({ period, ...filters })
      .then((data) => { if (live) { setReport(data); setError(null); } })
      .catch((err) => { if (live) setError(errorMessage(err, "Could not load reports.")); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [period, filters]);

  const fmt = useMemo(() => {
    const money = (v) => (v == null ? "—" : format(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 }));
    const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
    const num = (v) => String(round1(v));
    const hours = (v) => `${round1(v)}h`;
    const days = (v) => num(v);
    const compact = (v) => (v == null ? "—" : formatCompact(symbol, v));
    const cell = (kind, v) => ({ money, pct, hours, days, int: (x) => String(x ?? 0) }[kind] || ((x) => x || "—"))(v);
    return { money, pct, num, hours, days, cell, compact };
  }, [format, symbol]);

  const setTab = (value) => setParams((prev) => { const next = new URLSearchParams(prev); next.set("report", value); return next; }, { replace: true });
  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const exported = (what, kind) => setToast({ message: `${what} exported${kind === "csv" ? " as CSV (first sheet only)" : ""}.`, type: "success" });
  const [exporting, setExporting] = useState(null);
  const exportTabs = async (tabs, filename, format) => {
    const tables = [...new Set(tabs.flatMap(tablesOf))];
    const sheets = [summarySheet(tabs, report, fmt), ...tables.map((key) => tableSheet(key, report))];
    exported(tabs.length === 1 ? REPORT_TABS.find((t) => t.value === tabs[0]).label : "All reports", await exportWorkbook({ sheets, filename, format }));
  };

  if (showLoading) {
    return (
      <div className={ui.tableSkeleton}>
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className={`${ui.skeletonBlock} ${ui.tableSkeletonRow}`} />)}
      </div>
    );
  }

  const employees = (report?.filters.employees || []).filter((e) => filters.people === "all" || e.contractor === (filters.people === "contractor"));

  const charts = report && {
    pnl: () => (
      <Panel key="pnl" title="Revenue and labour cost" sub={`${report.period.label} · margin % shown above each month`}>
        {report.financial.monthly.some((m) => m.revenue || m.labour) ? (
          <BarChart
            ariaLabel="Revenue against labour cost per month"
            groups={report.financial.monthly.map((m) => ({ ...m, label: report.financial.monthly.length > 1 ? m.label.slice(0, 3) : m.label, note: m.margin == null ? "" : fmt.pct(m.margin) }))}
            series={[{ key: "revenue", label: "Revenue", color: "#185FA5" }, { key: "labour", label: "Labour cost", color: "#85B7EB" }]}
            noteTone={styles.good}
            format={fmt.money}
          />
        ) : <p className={styles.empty}>No revenue or approved hours in {report.period.label}.</p>}
      </Panel>
    ),
    topClients: () => (
      <Panel key="topClients" title="Top clients by revenue" sub="Recognised on projects">
        {report.financial.clients.length ? (
          <BarList ariaLabel="Top clients by revenue" rows={report.financial.clients.slice(0, 6).map((c) => ({ label: c.name, value: c.revenue, color: "#185FA5", display: fmt.compact(c.revenue) }))} />
        ) : <p className={styles.empty}>No revenue recognised in {report.period.label}.</p>}
      </Panel>
    ),
    utilisation: () => (
      <Panel key="utilisation" title="Billable utilisation by person" sub={`Line marks the ${fmt.pct(UTIL_TARGET)} target`}>
        {report.team.people.length ? (
          <BarList
            ariaLabel="Billable utilisation by person"
            max={1}
            target={UTIL_TARGET}
            rows={report.team.people.map((p) => ({ label: p.type === "Contractor" ? `${p.name} (contractor)` : p.name, value: p.utilisation || 0, color: utilColor(p.utilisation), display: fmt.pct(p.utilisation) }))}
          />
        ) : <p className={styles.empty}>No people match these filters.</p>}
      </Panel>
    ),
    stages: () => (
      <Panel key="stages" title="Pipeline by stage" sub="Value and win probability">
        {report.sales.by_stage.length ? (
          <>
            <BarList ariaLabel="Pipeline by stage" rows={report.sales.by_stage.map((s) => ({ label: `${s.status} (${s.count})`, value: s.value, color: STAGE_COLOR(s.probability), display: fmt.compact(s.value) }))} />
            <p className={styles.note}>
              Weighted pipeline {fmt.compact(report.sales.weighted_pipeline)} at {report.sales.by_stage.map((s) => fmt.pct(s.probability)).join(" / ")}.
            </p>
          </>
        ) : <p className={styles.empty}>No open leads.</p>}
      </Panel>
    ),
    clients: () => (
      <DonutPanel
        key="clients" title="Revenue by client" sub="Share of recognised revenue" centerSub="Recognised"
        slices={report.financial.clients.map((c) => ({ name: c.name, value: c.revenue }))}
        label={fmt.compact} show={(v, total) => fmt.pct(v / total)} empty={`No revenue recognised in ${report.period.label}.`}
      />
    ),
    forecast: () => (
      <Panel key="forecast" title="Revenue forecast" sub="Next 3 months">
        <BarChart
          ariaLabel="Booked revenue and weighted pipeline for the next three months"
          groups={report.financial.forecast.map((m) => ({ ...m, label: m.label.slice(0, 3), note: fmt.compact(m.booked + m.pipeline) }))}
          series={[{ key: "booked", label: "Booked on projects", color: "#185FA5" }, { key: "pipeline", label: "Weighted pipeline", hatched: true }]}
          noteTone={styles.brand}
          format={fmt.money}
        />
      </Panel>
    ),
    lostReasons: () => (
      <DonutPanel
        key="lostReasons" title="Why leads were lost" sub={report.period.label} centerSub="Lost"
        slices={report.sales.lost_reasons.map((r, i) => ({ name: r.reason, value: r.count, color: REASON_COLORS[i] }))}
        label={String} show={String} empty={`No leads lost in ${report.period.label}.`}
      />
    ),
    wonLost: () => (
      <Panel key="wonLost" title="Won and lost by month" sub={report.period.label}>
        <BarChart
          ariaLabel="Leads won and lost per month"
          groups={report.sales.by_month.map((m) => ({ ...m, label: report.sales.by_month.length > 1 ? m.label.slice(0, 3) : m.label, note: `${m.won}/${m.lost}` }))}
          series={[{ key: "won", label: "Won", color: "#16a34a" }, { key: "lost", label: "Lost", color: "#fca5a5" }]}
          format={String}
        />
      </Panel>
    ),
    projectStatus: () => (
      <DonutPanel
        key="projectStatus" title="Projects by status" sub="All projects" centerSub="Projects"
        slices={report.projects.by_status.map((s) => ({ name: s.status, value: s.count, color: s.color }))}
        label={String} show={String} empty="No projects yet."
      />
    ),
    hoursUsed: () => {
      const rows = report.projects.rows.filter((p) => p.budget_hours > 0).sort((a, b) => b.hours_used - a.hours_used).slice(0, 8);
      return (
        <Panel key="hoursUsed" title="Hours used vs estimate" sub="Red when over">
          {rows.length ? (
            <BarList
              ariaLabel="Hours used against the estimate per project"
              max={Math.max(1, ...rows.map((p) => p.hours_used))}
              rows={rows.map((p) => ({ label: p.project_name, value: p.hours_used, color: hoursColor(p.hours_used), display: fmt.pct(p.hours_used) }))}
            />
          ) : <p className={styles.empty}>No projects have an hours budget.</p>}
        </Panel>
      );
    },
    leaveTypes: () => (
      <DonutPanel
        key="leaveTypes" title="Days taken by type" sub={report.period.label} centerSub="Days"
        slices={report.leave.by_type.map((t) => ({ name: t.type, value: t.days }))}
        label={fmt.num} show={(v) => `${fmt.num(v)} days`} empty={`No approved leave in ${report.period.label}.`}
      />
    ),
    leaveMonths: () => (
      <Panel key="leaveMonths" title="Days taken by month" sub={report.period.label}>
        <BarChart
          ariaLabel="Leave days taken per month"
          groups={report.leave.by_month.map((m) => ({ ...m, label: report.leave.by_month.length > 1 ? m.label.slice(0, 3) : m.label, note: fmt.num(m.days) }))}
          series={[{ key: "days", label: "Days", color: "#185FA5" }]}
          format={fmt.num}
        />
      </Panel>
    ),
    recurring: () => (
      <Panel key="recurring" title="Monthly recurring tasks" sub={`Done on time, ${report.period.label}`}>
        {report.tasks.recurring.length ? (
          <BarList
            ariaLabel="Recurring tasks done on time"
            max={1}
            rows={report.tasks.recurring.map((s) => ({
              label: s.title,
              value: s.on_time / s.done,
              color: s.on_time === s.done ? "#16a34a" : "#f59e0b",
              display: `${s.on_time}/${s.done}`,
            }))}
          />
        ) : <p className={styles.empty}>{`No recurring tasks completed in ${report.period.label}.`}</p>}
      </Panel>
    ),
  };

  const renderItem = (item) => (item.startsWith("chart:")
    ? charts[item.slice(6)]()
    : <ReportTable key={item} tableKey={item} report={report} fmt={fmt} onExported={(n, kind) => exported(`${n} ${n === 1 ? "row" : "rows"}`, kind)} />);

  return (
    <div className={ui.fadeInContent}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className={ui.pageHeader}>
        <div className={ui.pageHeaderLeft} style={{ alignItems: "center" }}>
          <h1 className={ui.pageTitle}>Reports</h1>
          {report && <span className={styles.periodPill}>{report.period.label}</span>}
        </div>
        <div className={ui.pageHeaderRight}>
          <SegmentedControl ariaLabel="Period" options={PERIODS} value={period} onChange={setPeriod} />
          <RowActionsMenu actions={[{ label: "Export every report", onClick: () => report && setExporting({ tabs: REPORT_TABS.map((t) => t.value), filename: `reports-all-${period}` }) }]} />
          <button type="button" className={ui.addButton} disabled={!report} onClick={() => setExporting({ tabs: [tab], filename: `reports-${tab}-${period}` })}>
            Export…
          </button>
        </div>
      </div>

      {exporting && (
        <ExportDialog
          title="Export reports"
          onExport={({ format }) => exportTabs(exporting.tabs, exporting.filename, format)}
          onClose={() => setExporting(null)}
        />
      )}

      <div className={styles.tabs} role="tablist" aria-label="Reports">
        {REPORT_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            className={`${ui.tabButton} ${tab === t.value ? ui.activeTab : ""}`}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={ui.tableToolbar}>
        <CustomDropdown
          toolbar
          options={[{ value: "", label: "All Clients" }, ...(report?.filters.clients || []).map((c) => ({ value: String(c.id), label: c.name }))]}
          value={filters.client_id}
          onChange={(v) => setFilter({ client_id: v })}
        />
        <CustomDropdown toolbar options={PEOPLE} value={filters.people} onChange={(v) => setFilter({ people: v, employee_id: "" })} />
        <CustomDropdown
          toolbar
          options={[{ value: "", label: "All Employees" }, ...employees.map((e) => ({ value: String(e.id), label: e.name }))]}
          value={filters.employee_id}
          onChange={(v) => setFilter({ employee_id: v })}
        />
        <button type="button" className={`${ui.toolbarReset} ${styles.clear}`} onClick={() => setFilters(BLANK_FILTERS)}>Clear</button>
      </div>

      {error && <p className={styles.error} role="alert">{error}</p>}

      {report && (
        <div className={`${styles.body} ${loading ? styles.refreshing : ""}`} aria-busy={loading}>
          <div className={chrome.statRow}>
            {tabCards(tab, report, fmt).map((c) => <StatCard key={c.label} label={c.label} value={c.display} sub={c.sub} tone={c.tone} delta={c.delta} />)}
          </div>
          {TAB_LAYOUT[tab].map((item) => (Array.isArray(item)
            ? <div key={item.join()} className={styles.pair}>{item.map(renderItem)}</div>
            : renderItem(item)))}
        </div>
      )}
    </div>
  );
}
